import { Popover } from '@headlessui/react';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Icon } from '../../../common/Icon';
import { MenuButton, MenuOption } from '../../../common/MenuButton';
import { formatMarkdownContent } from '../../../common/TipTapEditor';
import { TicketUpdate } from '../types';

import { ProgressBar } from './ProgressBar';
import { ProgressPopover } from './ProgressPopover';

interface TicketUpdateCardProps {
  item: TicketUpdate;
  hideMenu: boolean;
  onClick: () => void;
  showDescription?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  actionButton?: JSX.Element;
  hidePopover?: boolean;
}

export function TicketUpdateCard({
  item,
  hideMenu,
  onClick,
  showDescription,
  onEdit,
  onDelete,
  actionButton,
  hidePopover,
}: TicketUpdateCardProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const handleDelete = (): void => {
    if (window.confirm('Are you sure you want to delete this ticket update?')) {
      try {
        onDelete?.();
        toast.success('Ticket update deleted successfully');
      } catch (error) {
        toast.error('Failed to delete ticket update');
      }
    }
  };

  const menuOptions = [
    { title: 'Edit', value: 'edit', onClick: onEdit },
    { title: 'Delete', value: 'delete', onClick: handleDelete, color: 'red' },
  ].filter((option): option is MenuOption => option.onClick !== undefined);

  const progressDiff = item.progress?.current
    ? item.progress.current - item.progress.previous
    : 0;

  return (
    <Popover className="relative w-full">
      <div
        className="text-[#434343] font-[400]"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col items-start flex-wrap gap-2 py-2 px-3 rounded-md border border-[#D9D9D9] bg-[#FEFDFC]  hover:bg-gray-50">
          <div
            className="flex flex-col w-full gap-2 cursor-pointer flex-wrap"
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
          >
            <div className="flex flex-row w-full items-center justify-between">
              <div className="flex flex-row items-center gap-2 min-w-0 flex-1">
                <Icon name="checkbox" className="text-[#1677FF]" />
                <h3 className="text-md font-normal leading-5 text-label truncate">
                  {item.title}
                </h3>
              </div>
              {!hideMenu && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <MenuButton options={menuOptions} direction="left" />
                </div>
              )}
            </div>
            {showDescription && (
              <p className="w-full text-md font-normal leading-6 text-label break-words whitespace-pre-wrap line-clamp-4">
                {formatMarkdownContent(item.description)}
              </p>
            )}
          </div>
          {actionButton && actionButton}
          {item.progress && (
            <ProgressBar
              previousProgress={item.progress?.previous || 0}
              currentProgress={item.progress?.current || 0}
            />
          )}
        </div>

        {!hidePopover && isHovered && item.progress && (
          <ProgressPopover
            progressDiff={progressDiff}
            previousProgress={item.progress?.previous || 0}
            currentProgress={item.progress?.current || 0}
          />
        )}
      </div>
    </Popover>
  );
}
